import { z } from 'zod';
import { dateTimeSchema, idSchema, nullableDateTimeSchema, paginatedSchema } from './common';
import { roleSchema } from './enums';
import { emailSchema } from './auth';

/** SPEC §9 — user administration (SUPER_ADMIN only). Password hashes never leave the server. */

export const userSummarySchema = z.object({
  id: idSchema,
  email: z.string().email(),
  name: z.string(),
  role: roleSchema,
  createdAt: dateTimeSchema,
  disabledAt: nullableDateTimeSchema,
  /** true when the user has never accepted their invite (no passwordHash yet). */
  pendingInvite: z.boolean(),
  inviteExpiresAt: nullableDateTimeSchema,
  lastSeenAt: nullableDateTimeSchema,
  activeSessions: z.number().int().min(0),
});
export type UserSummary = z.infer<typeof userSummarySchema>;

/** GET /api/users */
export const listUsersQuerySchema = z.object({
  includeDisabled: z.coerce.boolean().default(true),
  q: z.string().trim().max(200).optional(),
});
export type ListUsersQuery = z.infer<typeof listUsersQuerySchema>;

export const listUsersResponseSchema = z.object({ users: z.array(userSummarySchema) });
export type ListUsersResponse = z.infer<typeof listUsersResponseSchema>;

/** Paginated variant, kept for parity with other list endpoints. */
export const paginatedUsersResponseSchema = paginatedSchema(userSummarySchema);
export type PaginatedUsersResponse = z.infer<typeof paginatedUsersResponseSchema>;

/** POST /api/users/invite */
export const inviteUserRequestSchema = z.object({
  email: emailSchema,
  role: roleSchema.default('EMPLOYEE'),
  /** Optional display name pre-fill; the invitee can change it on accept. */
  name: z.string().trim().min(1).max(120).optional(),
});
export type InviteUserRequest = z.infer<typeof inviteUserRequestSchema>;

export const inviteUserResponseSchema = z.object({
  user: userSummarySchema,
  /** Full accept URL — shown once so the admin can send it manually (no mailer in v1). */
  inviteUrl: z.string().url(),
  inviteExpiresAt: dateTimeSchema,
});
export type InviteUserResponse = z.infer<typeof inviteUserResponseSchema>;

/** POST /api/users/:id/reinvite — regenerates the single-use token. */
export const reinviteUserResponseSchema = inviteUserResponseSchema;
export type ReinviteUserResponse = z.infer<typeof reinviteUserResponseSchema>;

/** POST /api/users/:id/disable — also deletes the user's sessions. */
export const disableUserResponseSchema = z.object({
  user: userSummarySchema,
  sessionsRevoked: z.number().int().min(0),
});
export type DisableUserResponse = z.infer<typeof disableUserResponseSchema>;

/** POST /api/users/:id/enable */
export const enableUserResponseSchema = z.object({ user: userSummarySchema });
export type EnableUserResponse = z.infer<typeof enableUserResponseSchema>;

/** PATCH /api/users/:id — role changes only (no self-demotion). */
export const updateUserRequestSchema = z.object({
  role: roleSchema.optional(),
  name: z.string().trim().min(1).max(120).optional(),
});
export type UpdateUserRequest = z.infer<typeof updateUserRequestSchema>;

export const updateUserResponseSchema = z.object({ user: userSummarySchema });
export type UpdateUserResponse = z.infer<typeof updateUserResponseSchema>;
